import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useStore } from '../store';

// Color palette for the color picker
const COLOR_PALETTE = [
  { name: 'None', value: 'transparent' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#E5E7EB' },
  { name: 'Gray', value: '#9CA3AF' },
  { name: 'Dark Gray', value: '#4B5563' },
  { name: 'Black', value: '#1F2937' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
];

// Font options
const FONT_FAMILIES = [
  { name: 'Default', value: 'inherit' },
  { name: 'Serif', value: 'Georgia, serif' },
  { name: 'Mono', value: 'ui-monospace, monospace' },
  { name: 'Handwritten', value: 'Comic Sans MS, cursive' },
];

const FONT_SIZES = [
  { name: 'S', value: 12, label: 'Small (12px)' },
  { name: 'M', value: 14, label: 'Medium (14px)' },
  { name: 'L', value: 18, label: 'Large (18px)' },
  { name: 'XL', value: 24, label: 'Extra Large (24px)' },
];

// Text overflow options
const TEXT_OVERFLOW_OPTIONS = [
  { name: 'Clip', value: 'hidden', label: 'Clip (hidden)' },
  { name: 'Overflow', value: 'visible', label: 'Overflow (visible)' },
];

// Opacity presets
const OPACITY_PRESETS = [
  { name: '100%', value: 1 },
  { name: '75%', value: 0.75 },
  { name: '50%', value: 0.5 },
  { name: '25%', value: 0.25 },
];

interface ColorPickerProps {
  color: string | undefined;
  onChange: (color: string) => void;
  label: string;
}

function ColorPicker({ color, onChange, label }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentColor = color || 'transparent';

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="style-control" ref={containerRef}>
      <span className="style-control-label">{label}</span>
      <button
        className="color-picker-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title={`Change ${label.toLowerCase()}`}
      >
        <span
          className="color-preview"
          style={{
            backgroundColor: currentColor === 'transparent' ? 'transparent' : currentColor,
          }}
        />
        <svg className="dropdown-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div className="color-dropdown">
          <div className="color-grid">
            {COLOR_PALETTE.map((c) => (
              <button
                key={c.value}
                className={`color-option ${currentColor === c.value ? 'selected' : ''}`}
                style={{
                  backgroundColor: c.value === 'transparent' ? 'transparent' : c.value,
                }}
                onClick={() => {
                  onChange(c.value);
                  setIsOpen(false);
                }}
                title={c.name}
              >
                {c.value === 'transparent' && (
                  <svg width="12" height="12" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                    <line x1="4" y1="4" x2="20" y2="20" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface SelectProps {
  value: string | undefined;
  onChange: (value: string) => void;
  options: { name: string; value: string | number; label?: string }[];
  label: string;
}

function Select({ value, onChange, options, label }: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentValue = value || String(options[0].value);
  const currentOption = options.find((o) => String(o.value) === currentValue) || options[0];

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="style-control" ref={containerRef}>
      <span className="style-control-label">{label}</span>
      <button
        className="select-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title={`Change ${label.toLowerCase()}`}
      >
        <span className="select-value">{currentOption.name}</span>
        <svg className="dropdown-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && (
        <div className="select-dropdown">
          {options.map((option) => (
            <button
              key={String(option.value)}
              className={`select-option ${currentValue === String(option.value) ? 'selected' : ''}`}
              onClick={() => {
                onChange(String(option.value));
                setIsOpen(false);
              }}
            >
              {option.label || option.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface OpacityControlProps {
  value: number | undefined;
  onChange: (value: number) => void;
}

function OpacityControl({ value, onChange }: OpacityControlProps) {
  const currentValue = value ?? 1;
  
  return (
    <div className="style-control">
      <span className="style-control-label">Opacity</span>
      <div className="opacity-control">
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={currentValue}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="opacity-slider"
        />
        <span className="opacity-value">{Math.round(currentValue * 100)}%</span>
      </div>
    </div>
  );
}

export function SelectionToolbar() {
  const selectedNodeId = useStore((state) => state.selectedNodeId);
  const selectedEdgeId = useStore((state) => state.selectedEdgeId);
  const nodes = useStore((state) => state.nodes);
  const connections = useStore((state) => state.connections);
  const updateNode = useStore((state) => state.updateNode);
  const updateConnection = useStore((state) => state.updateConnection);

  const selectedNode = selectedNodeId ? nodes[selectedNodeId] : null;
  const selectedEdge = selectedEdgeId ? connections.find((c) => c.id === selectedEdgeId) : null;

  const handleFillColorChange = useCallback(
    (color: string) => {
      if (selectedNodeId) {
        updateNode(selectedNodeId, { fillColor: color === 'transparent' ? undefined : color });
      }
    },
    [selectedNodeId, updateNode]
  );

  const handleStrokeColorChange = useCallback(
    (color: string) => {
      if (selectedNodeId) {
        updateNode(selectedNodeId, { strokeColor: color === 'transparent' ? undefined : color });
      } else if (selectedEdgeId) {
        updateConnection(selectedEdgeId, { strokeColor: color === 'transparent' ? undefined : color });
      }
    },
    [selectedNodeId, selectedEdgeId, updateNode, updateConnection]
  );

  const handleTextColorChange = useCallback(
    (color: string) => {
      if (selectedNodeId) {
        updateNode(selectedNodeId, { textColor: color === 'transparent' ? undefined : color });
      }
    },
    [selectedNodeId, updateNode]
  );

  const handleFontFamilyChange = useCallback(
    (fontFamily: string) => {
      if (selectedNodeId) {
        updateNode(selectedNodeId, { fontFamily: fontFamily === 'inherit' ? undefined : fontFamily });
      }
    },
    [selectedNodeId, updateNode]
  );

  const handleFontSizeChange = useCallback(
    (fontSize: string) => {
      if (selectedNodeId) {
        const size = parseInt(fontSize, 10);
        updateNode(selectedNodeId, { fontSize: size === 14 ? undefined : size });
      }
    },
    [selectedNodeId, updateNode]
  );

  const handleOpacityChange = useCallback(
    (opacity: number) => {
      if (selectedNodeId) {
        updateNode(selectedNodeId, { opacity: opacity === 1 ? undefined : opacity });
      } else if (selectedEdgeId) {
        updateConnection(selectedEdgeId, { opacity: opacity === 1 ? undefined : opacity });
      }
    },
    [selectedNodeId, selectedEdgeId, updateNode, updateConnection]
  );

  const handleTextOverflowChange = useCallback(
    (overflow: string) => {
      if (selectedNodeId) {
        updateNode(selectedNodeId, { 
          textOverflow: overflow === 'hidden' ? undefined : overflow as 'hidden' | 'visible' 
        });
      }
    },
    [selectedNodeId, updateNode]
  );

  // Don't render if nothing is selected
  if (!selectedNode && !selectedEdge) {
    return null;
  }

  // Determine which controls to show based on selection type
  const isShapeNode = selectedNode && ['rectangle', 'circle', 'diamond'].includes(selectedNode.type);
  const isTextNode = selectedNode && selectedNode.type === 'text';
  const isTaskLikeNode = selectedNode && ['task', 'note', 'milestone'].includes(selectedNode.type);
  const isEdge = !!selectedEdge;

  // Get a friendly name for the selection
  const getSelectionName = () => {
    if (isEdge) return 'Line';
    if (!selectedNode) return '';
    const typeNames: Record<string, string> = {
      task: 'Task',
      note: 'Note',
      milestone: 'Milestone',
      text: 'Text',
      rectangle: 'Rectangle',
      circle: 'Circle',
      diamond: 'Diamond',
    };
    return typeNames[selectedNode.type] || 'Element';
  };

  return (
    <div className="selection-toolbar">
      <div className="selection-toolbar-header">
        <span className="selection-type-badge">{getSelectionName()}</span>
      </div>
      
      <div className="selection-toolbar-controls">
        {/* Edge selected - stroke color and opacity */}
        {isEdge && (
          <>
            <ColorPicker
              color={selectedEdge?.strokeColor}
              onChange={handleStrokeColorChange}
              label="Color"
            />
            <OpacityControl
              value={(selectedEdge as any)?.opacity}
              onChange={handleOpacityChange}
            />
          </>
        )}

        {/* Shape nodes - fill, stroke, text color, font, opacity */}
        {isShapeNode && (
          <>
            <div className="style-section-label">Shape</div>
            <ColorPicker
              color={selectedNode?.fillColor}
              onChange={handleFillColorChange}
              label="Fill"
            />
            <ColorPicker
              color={selectedNode?.strokeColor}
              onChange={handleStrokeColorChange}
              label="Stroke"
            />
            <OpacityControl
              value={(selectedNode as any)?.opacity}
              onChange={handleOpacityChange}
            />
            <div className="style-section-label">Text</div>
            <ColorPicker
              color={selectedNode?.textColor}
              onChange={handleTextColorChange}
              label="Color"
            />
            <Select
              value={selectedNode?.fontFamily}
              onChange={handleFontFamilyChange}
              options={FONT_FAMILIES}
              label="Font"
            />
            <Select
              value={selectedNode?.fontSize?.toString()}
              onChange={handleFontSizeChange}
              options={FONT_SIZES}
              label="Size"
            />
            <Select
              value={selectedNode?.textOverflow || 'hidden'}
              onChange={handleTextOverflowChange}
              options={TEXT_OVERFLOW_OPTIONS}
              label="Overflow"
            />
          </>
        )}

        {/* Task/Note/Milestone - fill, stroke, text color, font, opacity */}
        {isTaskLikeNode && (
          <>
            <div className="style-section-label">Background</div>
            <ColorPicker
              color={selectedNode?.fillColor}
              onChange={handleFillColorChange}
              label="Fill"
            />
            <ColorPicker
              color={selectedNode?.strokeColor}
              onChange={handleStrokeColorChange}
              label="Stroke"
            />
            <OpacityControl
              value={(selectedNode as any)?.opacity}
              onChange={handleOpacityChange}
            />
            <div className="style-section-label">Text</div>
            <ColorPicker
              color={selectedNode?.textColor}
              onChange={handleTextColorChange}
              label="Color"
            />
            <Select
              value={selectedNode?.fontFamily}
              onChange={handleFontFamilyChange}
              options={FONT_FAMILIES}
              label="Font"
            />
            <Select
              value={selectedNode?.fontSize?.toString()}
              onChange={handleFontSizeChange}
              options={FONT_SIZES}
              label="Size"
            />
            <Select
              value={selectedNode?.textOverflow || 'hidden'}
              onChange={handleTextOverflowChange}
              options={TEXT_OVERFLOW_OPTIONS}
              label="Overflow"
            />
          </>
        )}

        {/* Text node - text color, font family, font size, opacity */}
        {isTextNode && (
          <>
            <ColorPicker
              color={selectedNode?.textColor}
              onChange={handleTextColorChange}
              label="Color"
            />
            <Select
              value={selectedNode?.fontFamily}
              onChange={handleFontFamilyChange}
              options={FONT_FAMILIES}
              label="Font"
            />
            <Select
              value={selectedNode?.fontSize?.toString()}
              onChange={handleFontSizeChange}
              options={FONT_SIZES}
              label="Size"
            />
            <Select
              value={selectedNode?.textOverflow || 'hidden'}
              onChange={handleTextOverflowChange}
              options={TEXT_OVERFLOW_OPTIONS}
              label="Overflow"
            />
            <OpacityControl
              value={(selectedNode as any)?.opacity}
              onChange={handleOpacityChange}
            />
          </>
        )}
      </div>
    </div>
  );
}
